/**
 * Hook de **exportación de órdenes a Excel** (`GET /export/today|all`).
 *
 * La exportación es una **acción** que produce un binario (no un dato remoto que
 * cachear), por eso es un `useMutation` y no un `useQuery`. La descarga del Excel
 * la sirve `lib/api` (ningún `fetch` suelto; `docs/frontend_architecture.md`,
 * Pr. 2): `exportOrders` devuelve el `blob` y el `filename`, y aquí se dispara la
 * descarga en el navegador con un `<a download>` y un object URL temporal (que se
 * revoca tras usarlo para no filtrar memoria). El gating de permiso (`all`) lo
 * decide la UI; el `ApiError` (404 sin órdenes, 500 sin openpyxl) se propaga y la
 * pantalla lo traduce con `adminErrorMessage`.
 */
import { useMutation } from "@tanstack/react-query";

import { exportOrders } from "../api";
import type { ExportFile, ExportScope } from "../types";

/**
 * Dispara la descarga de un blob en el navegador creando un `<a download>`
 * temporal. Se aísla para poder razonar sobre ella (y porque toca el DOM, a
 * diferencia del resto del hook). En jsdom (tests) `createObjectURL` no existe
 * por defecto: el test lo stubea, o comprueba el `filename`/llamada a la API.
 */
function triggerDownload(file: ExportFile): void {
  const url = URL.createObjectURL(file.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = file.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function useExportOrders() {
  return useMutation<ExportFile, Error, ExportScope>({
    mutationFn: (scope) => exportOrders(scope),
    onSuccess: (file) => {
      triggerDownload(file);
    },
  });
}
